/**
 * Renderizado uniforme de un `AsyncState<T>`: cargando, error, sin permiso,
 * vacio, listo.
 *
 * Por que existe
 * --------------
 * Con dos pantallas cada una escribia su cadena de `if (state.kind === ...)`.
 * Con diez, esa cadena es el lugar donde se cuelan los bugs caros y silenciosos:
 * la pantalla que muestra "cargando" para siempre porque nunca contemplo el
 * caso de error, o la que dice "no hay datos" cuando en realidad hubo un 403.
 * Este componente hace que ese orden sea imposible de equivocar.
 *
 * La distincion que no se puede perder es 403 vs cualquier otro error: un 403
 * no se reintenta, se pide acceso. Por eso se desvia a `<ForbiddenState>` en
 * vez de al cartel generico con boton "reintentar".
 *
 * Uso:
 *
 * ```tsx
 * const { state, reload } = useAsync(signal => listarCosas(api, { signal }), [api]);
 * return (
 *   <Async state={state} onRetry={reload} empty="No hay cosas todavia.">
 *     {(cosas) => <Tabla filas={cosas} />}
 *   </Async>
 * );
 * ```
 */

import type { ReactNode } from "react";

import { ApiError } from "../api/client";
import { ErrorState, ForbiddenState, Spinner } from "./Feedback";
import type { AsyncState } from "./useAsync";

export interface AsyncProps<T> {
  readonly state: AsyncState<T>;
  /** Que dibujar cuando hay datos. */
  readonly children: (data: T) => ReactNode;
  /** Recarga; si no se pasa, el estado de error no ofrece "reintentar". */
  readonly onRetry?: (() => void) | undefined;
  readonly loadingLabel?: string | undefined;
  /**
   * Que mostrar cuando la carga termino bien pero no trajo nada.
   * Si no se pasa, un resultado vacio se dibuja igual que uno lleno (una tabla
   * sin filas), que a veces es exactamente lo que se quiere.
   */
  readonly empty?: ReactNode | undefined;
  /**
   * Como decidir si `data` esta vacio. Por defecto: un array de longitud 0.
   * Se puede pasar otra cosa para respuestas paginadas (`data.items.length`).
   */
  readonly isEmpty?: ((data: T) => boolean) | undefined;
}

function defaultIsEmpty(data: unknown): boolean {
  return Array.isArray(data) && data.length === 0;
}

export function Async<T>({
  state,
  children,
  onRetry,
  loadingLabel,
  empty,
  isEmpty = defaultIsEmpty,
}: AsyncProps<T>) {
  if (state.kind === "idle" || state.kind === "loading") {
    return <Spinner {...(loadingLabel === undefined ? {} : { label: loadingLabel })} />;
  }

  if (state.kind === "error") {
    if (state.error instanceof ApiError && state.error.isForbidden) {
      return <ForbiddenState detail={state.error.detail} />;
    }
    return <ErrorState error={state.error} onRetry={onRetry} />;
  }

  if (empty !== undefined && isEmpty(state.data)) {
    return <p className="feedback feedback--empty">{empty}</p>;
  }

  return <>{children(state.data)}</>;
}
